import { Form, Formik } from "formik";
import { UploadIcon } from "vega-icons";
import * as Yup from "yup";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { FormLabel } from "@vega-core/components/Form/Layout/FormLabel";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { Input } from "@vega-core/components/shadcn/input";
import { m } from "@vega-core/paraglide/messages";

import { NDA_ACCEPT_MIME_TYPES } from "../../constants/ndaConstants";
import { useLpNdaUpload } from "../../hooks/useLpNdaUpload";

type RevisedNdaUploadModalProps = {
  isOpen: boolean;
  onClose: () => void;
  limitedPartnerId: string;
  dealId: string;
};

type FormValues = {
  selectedFile: File | null;
};

const initialValues: FormValues = {
  selectedFile: null,
};

export const RevisedNdaUploadModal = ({
  isOpen,
  onClose,
  limitedPartnerId,
  dealId,
}: RevisedNdaUploadModalProps) => {
  const { handleUpload, isUploading } = useLpNdaUpload({
    dealId,
    onSuccess: onClose,
  });

  const validationSchema = Yup.object({
    selectedFile: Yup.mixed().required(
      m.formValidation_required({
        name: m.coInvestments_dealReview_nda(),
      }),
    ),
  });

  const handleFormSubmit = (values: FormValues) => {
    if (values.selectedFile) {
      handleUpload(limitedPartnerId, values.selectedFile);
    }
  };

  return (
    <ActionaryModal
      dataTestId="revised-nda-upload-modal"
      fluidHeight
      open={isOpen}
      setOpen={onClose}
      title={m.coInvestments_tracking_addRevisedNda()}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        validationSchema={validationSchema}
      >
        {({ setFieldValue, isValid, dirty }) => (
          <Form className="space-y-6">
            <div className="space-y-2">
              <FormLabel
                forName="revised-nda-file-input"
                label={m.coInvestments_ndaFile_pdfOrWord()}
              />
              <Input
                accept={NDA_ACCEPT_MIME_TYPES}
                className="cursor-pointer file:text-foreground px-2 text-base file:inline-flex file:h-5 file:border-0 file:bg-transparent file:text-sm file:font-medium md:text-sm"
                disabled={isUploading}
                id="revised-nda-file-input"
                onChange={(event) => {
                  const [file] = event.target.files ?? [];
                  setFieldValue("selectedFile", file);
                }}
                type="file"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <ThemedButton
                dataTestId="cancel-revised-nda-upload"
                disabled={isUploading}
                onClick={onClose}
                variant="secondary"
              >
                {m.common_cancel()}
              </ThemedButton>
              <ThemedButton
                dataTestId="upload-revised-nda-button"
                disabled={!isValid || !dirty || isUploading}
                icon={<UploadIcon className="w-4 h-4" />}
                isLoading={isUploading}
                type="submit"
              >
                {m.common_upload()}
              </ThemedButton>
            </div>
          </Form>
        )}
      </Formik>
    </ActionaryModal>
  );
};
